'use client';

import { useMemo, useState, useCallback } from 'react';
import type { ChatWidgetProps } from './types';
import type { FilterGroup } from '../search/types';
import { useChat } from './hooks/useChat';
import ChatHeader from './ChatHeader';
import ChatSidebar from './ChatSidebar';
import ChatProductGrid from './ChatProductGrid';
import FiltersSidebar from '../search/Sidebar/FiltersSidebar';

export default function ChatWidget({ onClose, onSwitchToSearch }: ChatWidgetProps) {
  const { messages, products, isLoading, searchProgress, sendMessage } = useChat();

  // Client-side filter state
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  // Build filter groups from the products returned by chat
  const filterGroups = useMemo<FilterGroup[]>(() => {
    if (products.length === 0) return [];

    const categories = new Map<string, number>();
    const brands = new Map<string, number>();

    for (const p of products) {
      if (p.category) {
        categories.set(p.category, (categories.get(p.category) || 0) + 1);
      }
      if (p.brand) {
        brands.set(p.brand, (brands.get(p.brand) || 0) + 1);
      }
    }

    const groups: FilterGroup[] = [];

    if (categories.size > 0) {
      groups.push({
        title: 'Κατηγορίες',
        items: Array.from(categories.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([name, count]) => ({ name, count })),
        maxVisible: 5,
      });
    }

    if (brands.size > 0) {
      groups.push({
        title: 'Brand',
        items: Array.from(brands.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([name, count]) => ({ name, count })),
        maxVisible: 5,
      });
    }

    return groups;
  }, [products]);

  // Client-side filtering of displayed products
  const displayProducts = useMemo(() => {
    if (selectedFilters.length === 0) return products;
    return products.filter(p => {
      return selectedFilters.some(
        f => p.category === f || p.brand === f
      );
    });
  }, [products, selectedFilters]);

  const handleFilterToggle = useCallback((_groupTitle: string, itemName: string) => {
    setSelectedFilters(prev =>
      prev.includes(itemName)
        ? prev.filter(f => f !== itemName)
        : [...prev, itemName]
    );
  }, []);

  return (
    <div className="flex flex-col h-full bg-white">
      <ChatHeader onClose={onClose} onSwitchToSearch={onSwitchToSearch} />

      {/* Three-column layout */}
      <div className="flex flex-1 min-h-0">
        {/* Left: Filters sidebar — only show when we have products */}
        {filterGroups.length > 0 && (
          <div className="hidden xl:block flex-shrink-0 w-[250px] border-r border-[#E5E7EB]">
            <div className="h-full overflow-y-auto overscroll-contain custom-scrollbar">
              <FiltersSidebar
                groups={filterGroups}
                selectedItems={selectedFilters}
                onFilterToggle={handleFilterToggle}
              />
            </div>
          </div>
        )}

        {/* Center: Product grid */}
        <div className="flex-1 min-w-0 overflow-y-auto overscroll-contain custom-scrollbar">
          <ChatProductGrid
            products={displayProducts}
            isLoading={!!searchProgress}
          />
        </div>

        {/* Right: Chat sidebar */}
        <ChatSidebar
          messages={messages}
          isLoading={isLoading}
          searchProgress={searchProgress}
          onSendMessage={sendMessage}
        />
      </div>
    </div>
  );
}
